// features/pos/components/CategoryPanel.tsx
import { motion, AnimatePresence } from 'framer-motion';
import { Package, X, ChevronLeft, Tag, Gift } from 'lucide-react';
import { clsx } from 'clsx';

interface Category {
    id: string; name: string; slug: string; parentId: string | null; color?: string;
}

interface CategoryPanelProps {
    categories: Category[];
    selectedId: string;
    onSelect: (id: string) => void;
    isOpen: boolean;
    onToggle: () => void;
}

export function CategoryPanel({ categories, selectedId, onSelect, isOpen, onToggle }: CategoryPanelProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 220, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    className="bg-white border-r border-slate-200 flex-shrink-0 overflow-hidden flex flex-col"
                >
                    {/* Panel Header */}
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
                        <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                            Categories
                        </p>
                        <button
                            onClick={onToggle}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                            type="button"
                        >
                            <ChevronLeft size={14} />
                        </button>
                    </div>

                    {/* Category List */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {/* All Products */}
                        <button
                            onClick={() => onSelect('')}
                            className={clsx(
                                'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left',
                                !selectedId
                                    ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                                    : 'text-slate-600 hover:bg-slate-50'
                            )}
                            type="button"
                        >
                            <div className={clsx(
                                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                                !selectedId ? 'bg-emerald-100' : 'bg-slate-100'
                            )}>
                                <Package size={16} className={!selectedId ? 'text-emerald-600' : 'text-slate-400'} />
                            </div>
                            <span className="truncate">All Products</span>
                            <span className="ml-auto text-xs text-slate-400">{categories.length}</span>
                        </button>

                        {/* Clearance Sale Category */}
                        <button
                            onClick={() => onSelect('clearance')}
                            className={clsx(
                                'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left',
                                selectedId === 'clearance'
                                    ? 'bg-amber-50 text-amber-800 shadow-sm border border-amber-200'
                                    : 'text-amber-700 hover:bg-amber-50/60'
                            )}
                            type="button"
                        >
                            <div className={clsx(
                                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                                selectedId === 'clearance' ? 'bg-amber-200 text-amber-800' : 'bg-amber-100 text-amber-700'
                            )}>
                                <Tag size={16} />
                            </div>
                            <span className="truncate font-semibold">Clearance</span>
                            <span className="ml-auto text-[10px] font-bold bg-amber-200/80 text-amber-800 px-1.5 py-0.5 rounded-full">
                                SALE
                            </span>
                        </button>

                        {/* Gift Hampers Category */}
                        <button
                            onClick={() => onSelect('hampers')}
                            className={clsx(
                                'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left',
                                selectedId === 'hampers'
                                    ? 'bg-purple-50 text-purple-800 shadow-sm border border-purple-200'
                                    : 'text-purple-700 hover:bg-purple-50/60'
                            )}
                            type="button"
                        >
                            <div className={clsx(
                                'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                                selectedId === 'hampers' ? 'bg-purple-200 text-purple-800' : 'bg-purple-100 text-purple-700'
                            )}>
                                <Gift size={16} />
                            </div>
                            <span className="truncate font-semibold">Hampers</span>
                            <span className="ml-auto text-[10px] font-bold bg-purple-200/80 text-purple-800 px-1.5 py-0.5 rounded-full">
                                🎁
                            </span>
                        </button>

                        {/* Category Items */}
                        {categories.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => onSelect(cat.id)}
                                className={clsx(
                                    'w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left',
                                    selectedId === cat.id
                                        ? 'bg-emerald-50 text-emerald-700 shadow-sm'
                                        : 'text-slate-600 hover:bg-slate-50'
                                )}
                                type="button"
                            >
                                <div
                                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{
                                        backgroundColor: selectedId === cat.id
                                            ? (cat.color || '#0D9488') + '20'
                                            : '#f1f5f9'
                                    }}
                                >
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: cat.color || '#0D9488' }}
                                    />
                                </div>
                                <span className="truncate">{cat.name}</span>
                            </button>
                        ))}
                    </div>

                    {/* Panel Footer */}
                    <div className="px-4 py-2.5 border-t border-slate-100 flex-shrink-0">
                        <p className="text-[10px] text-slate-400 text-center">
                            {categories.length} categor{categories.length === 1 ? 'y' : 'ies'}
                        </p>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}